/**
 * Clock API schemas — OpenAPI surfaces over the CLOCKS registry (Rule 1).
 *
 * The definition schema and every example are DERIVED from the registry
 * (registries/clocks ← schema/clocks.json), never typed here. The previous
 * version of this file carried a fabricated example (an invented clock id
 * pairing a 25-school-day value with the IEE citation and a consent trigger)
 * that blended THREE real clocks into one false legal claim — and its enums
 * could not even represent the canonical registry vocabulary. An API example is shipped
 * text: it is read as canon by every consumer of the docs. So the example
 * IS a canon row (`iee_decision_due`), resolved by id at module load —
 * registry drift breaks the build, never the truth (TASK-00034 item 6).
 */
import { z } from '@hono/zod-openapi'
import {
  ClockEntrySchema,
  ClockEntryKindSchema,
  ClockEntryBoundSchema,
  ClockEntryUnitSchema,
  getClock,
} from '../registries/clocks'

/** A REAL registry row — the OpenAPI example is canon by construction. */
const EXAMPLE_CLOCK = getClock('iee_decision_due')

export const ClockKindSchema = ClockEntryKindSchema.openapi({
  example: EXAMPLE_CLOCK.kind,
  description:
    "Source of authority for the clock: 'regulatory' (backed by a CITATIONS registry id with a verification status), 'district' (policy, NOT law — cite is empty), 'practice' (professional convention, not law)",
})

export const ClockBoundSchema = ClockEntryBoundSchema.openapi({
  example: EXAMPLE_CLOCK.bound,
  description:
    "'max' = deadline (tighter is safer) · 'min' = floor (must provide AT LEAST this much — an entitlement is not a deadline) · 'exact' = hard date",
})

export const ClockUnitSchema = ClockEntryUnitSchema.openapi({
  example: EXAMPLE_CLOCK.unit,
  description:
    "Unit the clock runs in. 'school' days require an explicit district calendar; 'months'/'years' clocks are intervals, never day math.",
})

export const ClockDefinitionSchema = ClockEntrySchema.openapi('ClockDefinition', {
  description:
    'A statutory deadline or district policy timer, resolved from the CLOCKS registry (schema/clocks.json). Definitions are registry rows — never authored through the API.',
  example: EXAMPLE_CLOCK,
})

export const ClockInstanceSchema = z.object({
  id: z.string().uuid(),
  clockId: z.string().min(1).max(64),
  studentId: z.string().uuid(),
  districtId: z.string().min(1).max(64),
  triggerEvent: z.string(),
  triggerAt: z.string().datetime(),
  dueAt: z.string().datetime().nullable(),
  status: z.enum(['running', 'met', 'overdue', 'waived']),
  daysElapsed: z.number().int().nullable(),
  daysRemaining: z.number().int().nullable(),
  lane: z.enum(['mtss', 'sped', 's504', 'el', 'health']).nullable(),
}).openapi('ClockInstance', { description: 'A running clock instance for a student' })

export const ClockComplianceScanSchema = z.object({
  studentId: z.string().uuid(),
  lane: ClockInstanceSchema.shape.lane,
  clocks: z.array(ClockInstanceSchema),
  overall: z.enum(['compliant', 'at-risk', 'overdue', 'unknown']),
  scanTimestamp: z.string().datetime(),
}).openapi('ClockComplianceScan', { description: 'Compliance scan result for a student' })

export type ClockKind = z.infer<typeof ClockKindSchema>
export type ClockBound = z.infer<typeof ClockBoundSchema>
export type ClockUnit = z.infer<typeof ClockUnitSchema>
export type ClockDefinition = z.infer<typeof ClockDefinitionSchema>
export type ClockInstance = z.infer<typeof ClockInstanceSchema>
export type ClockComplianceScan = z.infer<typeof ClockComplianceScanSchema>
